import { bpdLineFromEllipse } from '../../../ComponnentsUI/DrawingCanvas/utils/ellipseBpdUtils';
import type { CategoryDef } from '../../../../helpers/api/config/config';
import {
  findCategoryByCode,
  findHcEllipseCategory,
  getLengthMeasForCategory,
  getPairedBiometryCategoryIds,
} from '../../../../helpers/api/config/measConfig';

export type RealtimeStructureToDraw = {
  label: string;
  category_id?: number;
  points: { x: number; y: number }[];
  type?: string;
  color?: string | null;
  area?: number;
  perimeter?: number;
  length?: number;
  score?: number;
  derivedFrom?: string;
};

export type BpdOverlayCategoryContext = {
  categories?: CategoryDef[];
  categoriesByCode?: Record<string, number>;
  measLabelToCategoryId?: Record<string, number>;
};

export type EllipseGeometry = {
  center: { x: number; y: number };
  radiusX: number;
  radiusY: number;
  rotation?: number;
};

function normalizeStructureLabel(label: unknown): string {
  return String(label ?? '')
    .replace(/_not_visible$/i, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
}

function resolveHcCategory(
  ctx?: BpdOverlayCategoryContext
): CategoryDef | undefined {
  const categories = ctx?.categories ?? [];
  if (!categories.length) return undefined;
  return findHcEllipseCategory(categories);
}

function resolveHcCategoryId(ctx?: BpdOverlayCategoryContext): number | undefined {
  const cat = resolveHcCategory(ctx);
  if (cat) return cat.id;
  const fromCode = ctx?.categoriesByCode?.fetal_brain;
  return fromCode ?? ctx?.measLabelToCategoryId?.MEAS__HC;
}

function resolveLengthMeas(ctx?: BpdOverlayCategoryContext): string {
  const cat = resolveHcCategory(ctx);
  return getLengthMeasForCategory(cat) ?? 'MEAS__BPD';
}

/** Parse ellipse geometry from realtime overlay points (2/3/4+ point encodings). */
export function ellipseGeometryFromDrawPoints(
  points: { x: number; y: number }[],
  geometryType?: string
): EllipseGeometry | null {
  if (!points?.length) return null;

  if (points.length === 3) {
    const c = points[0];
    const majorEnd = points[1];
    const minorEnd = points[2];
    const rx = Math.hypot(majorEnd.x - c.x, majorEnd.y - c.y);
    const ry = Math.hypot(minorEnd.x - c.x, minorEnd.y - c.y);
    const rotation = Math.atan2(majorEnd.y - c.y, majorEnd.x - c.x);
    if (rx <= ry) {
      return { center: c, radiusX: rx, radiusY: ry, rotation };
    }
    return {
      center: c,
      radiusX: ry,
      radiusY: rx,
      rotation: rotation + Math.PI / 2,
    };
  }

  if (points.length >= 2) {
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return {
      center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
      radiusX: Math.max(1, (maxX - minX) / 2),
      radiusY: Math.max(1, (maxY - minY) / 2),
      rotation: 0,
    };
  }

  return null;
}

function ellipseAreaScore(structure: RealtimeStructureToDraw): number {
  const geom = ellipseGeometryFromDrawPoints(structure.points, structure.type);
  if (!geom) return 0;
  return geom.radiusX * geom.radiusY;
}

function isHcEllipseStructure(
  structure: RealtimeStructureToDraw,
  ctx?: BpdOverlayCategoryContext
): boolean {
  const geo = String(structure.type ?? '').toUpperCase();
  const isEllipse = geo === 'ELLIPSE' || geo === 'CIRCLE';
  if (!isEllipse || !structure.points?.length) return false;

  const label = normalizeStructureLabel(structure.label);
  const hcCategoryId = resolveHcCategoryId(ctx);
  const pairedIds = getPairedBiometryCategoryIds(ctx?.categories ?? []);

  if (hcCategoryId != null && structure.category_id === hcCategoryId) {
    return true;
  }
  if (pairedIds.size > 0 && structure.category_id != null) {
    return pairedIds.has(structure.category_id);
  }
  if (label === 'fetal_brain') return true;
  const fetalBrainCode = findCategoryByCode(ctx?.categories ?? [], 'fetal_brain');
  if (fetalBrainCode && label === normalizeStructureLabel(fetalBrainCode.code)) {
    return true;
  }
  return false;
}

function hasBpdLineAlready(
  structures: RealtimeStructureToDraw[],
  ctx?: BpdOverlayCategoryContext
): boolean {
  const hcCategoryId = resolveHcCategoryId(ctx);
  const lengthMeas = resolveLengthMeas(ctx).toLowerCase();

  return structures.some((s) => {
    const geo = String(s.type ?? '').toUpperCase();
    if (geo !== 'LINE' && geo !== 'POLYLINE') return false;
    const label = normalizeStructureLabel(s.label);
    if (label.includes('bpd') || label === lengthMeas) return true;
    if (s.derivedFrom === 'hc_ellipse') return true;
    if (hcCategoryId != null && s.category_id === hcCategoryId && s.points?.length === 2) {
      return true;
    }
    return false;
  });
}

/**
 * Realtime inference sends HC as a structure ellipse only.
 * BPD is computed as a measurement value — add the derived minor-axis line for display.
 * Category ids and meas names come from /config/categories (no hardcoded ids).
 */
export function appendDerivedBpdLineToStructures(
  structures: RealtimeStructureToDraw[],
  ctx?: BpdOverlayCategoryContext
): RealtimeStructureToDraw[] {
  if (!structures.length) return structures;
  if (hasBpdLineAlready(structures, ctx)) return structures;

  const hcCandidates = structures.filter((s) => isHcEllipseStructure(s, ctx));
  if (!hcCandidates.length) return structures;

  const hcEllipse =
    hcCandidates.length === 1
      ? hcCandidates[0]
      : hcCandidates
          .slice()
          .sort((a, b) => ellipseAreaScore(b) - ellipseAreaScore(a))[0];

  const geom = ellipseGeometryFromDrawPoints(hcEllipse.points, hcEllipse.type);
  if (!geom) return structures;

  const hcCategoryId = resolveHcCategoryId(ctx) ?? hcEllipse.category_id;
  const lengthMeas = resolveLengthMeas(ctx);
  const bpd = bpdLineFromEllipse(geom);

  return [
    ...structures,
    {
      label: lengthMeas,
      category_id: hcCategoryId,
      type: 'LINE',
      points: [bpd.startPoint, bpd.endPoint],
      color: hcEllipse.color ?? null,
      score: hcEllipse.score,
      derivedFrom: 'hc_ellipse',
    },
  ];
}
